/**
 * Timeline Slider with Thumbnail Preview
 *
 * Combines the seek slider with hover-based thumbnail preview using VTT sprite sheets
 */

import type { ReactElement } from "react";
import { Slider } from "@lumi/ui-headless";
import React, { useCallback, useRef, useState } from "react";
import { formatTime } from "../../../shared/utils/time";
import { TimelineThumbnailSprite } from "./TimelineThumbnailSprite";

/**
 * Default scale for thumbnail preview (2x = 320x180 from 160x90 base)
 */
const THUMBNAIL_SCALE = 2;

/**
 * Throttle interval for thumbnail updates (ms) - reduces updates for better performance
 */
const THUMBNAIL_UPDATE_THROTTLE_MS = 16; // ~60fps

/**
 * Delay before showing thumbnail preview (ms) - prevents flashing when cursor passes over
 */
const THUMBNAIL_SHOW_DELAY_MS = 300;

export type TimelineSliderProps = Readonly<{
  time: number;
  duration: number;
  onSeek: (nextSeconds: number) => void;
  /** VTT sprite sheet URL (from asset.thumbnailVttUrl) */
  vttUrl?: string | null;
}>;

export const TimelineSlider = React.memo(function TimelineSlider({
  time,
  duration,
  onSeek,
  vttUrl,
}: TimelineSliderProps): ReactElement {
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [thumbnailVisible, setThumbnailVisible] = useState(false);
  const [thumbnailLeftOffset, setThumbnailLeftOffset] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const lastUpdateRef = useRef<number>(0);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle mouse movement over timeline for thumbnail preview (throttled)
  const handleTimelineMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!sliderRef.current || duration <= 0 || !vttUrl) return;

      // Throttle updates to ~60fps for better performance
      const now = Date.now();
      if (now - lastUpdateRef.current < THUMBNAIL_UPDATE_THROTTLE_MS) {
        return;
      }
      lastUpdateRef.current = now;

      const rect = sliderRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const percent = Math.max(0, Math.min(1, x / rect.width));
      const timeAtPosition = percent * duration;

      setHoverTime(timeAtPosition);
      // Pass the exact clientX position for absolute positioning
      setThumbnailLeftOffset(event.clientX);

      // Only show thumbnail after a short delay to prevent flashing on quick cursor passes
      if (!thumbnailVisible && !showTimeoutRef.current) {
        showTimeoutRef.current = setTimeout(() => {
          setThumbnailVisible(true);
          showTimeoutRef.current = null;
        }, THUMBNAIL_SHOW_DELAY_MS);
      }
    },
    [duration, vttUrl, thumbnailVisible],
  );

  const handleTimelineMouseLeave = useCallback(() => {
    // Clear any pending show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    setThumbnailVisible(false);
    setHoverTime(null);
  }, []);

  return (
    <>
      {/* Timeline Thumbnail Preview using VTT Sprite Sheets */}
      <TimelineThumbnailSprite
        vttUrl={vttUrl ?? null}
        hoverTime={hoverTime}
        _duration={duration}
        leftOffset={thumbnailLeftOffset}
        visible={thumbnailVisible}
        scale={THUMBNAIL_SCALE}
      />

      <div className="flex pl-8 items-center gap-4">
        <span className="tabular-nums text-sm text-neutral-300 min-w-[4rem]">{formatTime(time)}</span>
        <div // NOSONAR
          ref={sliderRef}
          className="flex-1 px-1 py-3 -my-3"
          onMouseMove={handleTimelineMouseMove}
          onMouseLeave={handleTimelineMouseLeave}
        >
          <Slider
            value={Math.floor(time)}
            min={0}
            max={Math.max(0, Math.floor(duration))}
            step={1}
            ariaLabel="Seek video timeline"
            onChange={onSeek}
          />
        </div>
        <span className="tabular-nums text-sm text-neutral-300 min-w-[4rem]">{formatTime(duration)}</span>
      </div>
    </>
  );
});
